import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { RECOVERABLE_SUBSCRIPTION_STATUSES } from '@quoter/shared/schemas'
import { supabase } from '../lib/supabase'
import { useSession } from '../session/useSession'
import { useBillingPortal } from '../hooks/useBilling'
import { pricingPlansQueryOptions, productQueryOptions } from '../session/queries'
import { callPublicFunction } from '../lib/functions'
import { getProduct } from '../config'
import { EVENTS, track } from '../lib/analytics'
import { useI18n } from '../i18n/LocaleContext'
import { Button } from '../ui/Button'
import { Logo } from '../ui/Logo'
import { LoadingBlock } from '../ui/Feedback'
import { PlanPicker } from '../ui/PlanPicker'

/**
 * Where a signed-in user lands when they have no workspace yet (checkout never
 * finished) or their subscription lapsed. Their data is untouched — this is a
 * gate, not a teardown.
 *
 * Three cases, in order of how often they happen:
 *   past_due / unpaid / incomplete → the card failed. Send the admin to the
 *                                    Stripe portal to fix it; a new checkout
 *                                    would create a second subscription.
 *   canceled                       → a fresh checkout. The webhook attaches it
 *                                    to this same workspace.
 *   no profile at all              → checkout never completed; offer it again.
 */
export function Paywall() {
  const session = useSession()
  const navigate = useNavigate()
  const plansQuery = useQuery(pricingPlansQueryOptions)
  const portal = useBillingPortal()
  const { t, locale } = useI18n()
  const trialDays = useQuery(productQueryOptions).data?.trial_days ?? getProduct().trialDays

  const [pendingPlanId, setPendingPlanId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (session.status === 'authenticated') navigate('/', { replace: true })
    if (session.status === 'unauthenticated') navigate('/login', { replace: true })
  }, [session.status, navigate])

  const email =
    session.status === 'unprovisioned' || session.status === 'no_subscription'
      ? (session.user.email ?? '')
      : ''

  const lapsed = session.status === 'no_subscription' ? session : null
  const recoverable = !!lapsed && RECOVERABLE_SUBSCRIPTION_STATUSES.has(lapsed.subscriptionStatus ?? '')
  const isAdmin = lapsed ? lapsed.isAdmin : true

  async function startCheckout(planId: string) {
    if (pendingPlanId) return
    setError(null)
    setPendingPlanId(planId)

    // Pass the signed-in address so Stripe pre-fills it and the webhook
    // reconnects the new subscription to this same auth user.
    const result = await callPublicFunction<{ checkoutUrl: string }>('stripe-checkout', {
      product: getProduct().id,
      pricingPlanId: planId,
      email: email || undefined,
      locale,
    })
    if (!result.ok || !result.data.checkoutUrl) {
      setError(t.pricing.checkoutError)
      setPendingPlanId(null)
      return
    }
    track(EVENTS.checkoutStarted, { plan: planId })
    window.location.href = result.data.checkoutUrl
  }

  async function signOut() {
    await supabase.auth.signOut()
    navigate('/login', { replace: true })
  }

  if (session.status === 'loading') {
    return (
      <main className="flex min-h-screen items-center justify-center bg-canvas">
        <LoadingBlock />
      </main>
    )
  }

  const plans = plansQuery.data ?? []

  const title = !lapsed
    ? t.paywall.pendingTitle
    : recoverable
      ? t.paywall.recoverableTitle
      : t.paywall.title
  const body = !lapsed
    ? t.paywall.pendingBody
    : !isAdmin
      ? t.paywall.memberBody
      : recoverable
        ? t.paywall.recoverableBody
        : t.paywall.body

  return (
    <main className="min-h-screen bg-canvas px-4 py-10">
      <div className="mx-auto w-full max-w-3xl space-y-6">
        <div className="flex justify-center">
          <Logo />
        </div>

        <div className="mx-auto flex w-full max-w-lg items-center justify-between rounded-lg border border-DEFAULT bg-surface px-4 py-3 text-[12.5px]">
          <span className="truncate text-gray-500">
            {t.paywall.signedInAs} <span className="font-medium text-gray-900">{email}</span>
          </span>
          <button
            type="button"
            onClick={() => void signOut()}
            className="shrink-0 text-gray-500 underline underline-offset-2 hover:text-gray-900"
          >
            {t.auth.signOut}
          </button>
        </div>

        <div className="text-center">
          <h1 className="font-display text-[34px] font-normal tracking-tightish text-gray-900">{title}</h1>
          <p className="mt-2 text-[13.5px] text-gray-500">{body}</p>
        </div>

        {lapsed && !isAdmin ? null : (
          <>
            {recoverable && (
              <div className="mx-auto w-full max-w-lg rounded-xl bg-surface p-6 shadow-card">
                <Button
                  className="w-full"
                  size="lg"
                  disabled={portal.opening}
                  onClick={() => void portal.openPortal('/paywall')}
                >
                  {portal.opening ? t.settings.openingPortal : t.paywall.updatePayment}
                </Button>
                {portal.error && (
                  <p className="mt-3 text-center text-[13px] text-red-700" role="alert">
                    {t.paywall.portalError}
                  </p>
                )}
                <p className="mt-5 text-center text-[12px] text-gray-400">{t.paywall.orStartNew}</p>
              </div>
            )}

            {plansQuery.isLoading && <LoadingBlock />}
            {error && (
              <p className="text-center text-[13px] text-red-700" role="alert">
                {error}
              </p>
            )}

            {plans.length > 0 && (
              <PlanPicker
                plans={plans}
                trialDays={trialDays}
                pendingPlanId={pendingPlanId}
                onSelect={(planId) => void startCheckout(planId)}
                variant={recoverable ? 'secondary' : 'primary'}
              />
            )}
          </>
        )}
      </div>
    </main>
  )
}
